<template>
  <div class="page-wrapper">
    <Header></Header>
    
    <div class="main-content">
      <nav class="sidebar-nav">
        <div class="sidebar-sticky">
          <h3 class="nav-title">目录</h3>
          <ul>
            <li v-for="section in sections" :key="'nav-' + section.title" v-if="section.title">
              <a :href="'#' + formatId(section.title)" class="nav-link">{{ section.title }}</a>
            </li>
          </ul>
        </div>
      </nav>

      <div class="dta-container">
        <header class="policy-header">
          <h1 class="title">Data Privacy Policy and Transfer Agreement</h1>
          <div class="version-badge">Version 1.0, August 12, 2025</div>
          <p class="intro-text">
            This agreement outlines the protocols for data handling, security measures, and user responsibilities within the PGS-hub platform.
          </p>
        </header>

        <hr class="divider" />

        <div class="content-body">
          <section 
            v-for="section in sections" 
            :key="section.title" 
            :id="formatId(section.title)"
            class="section"
          >
            <h2 v-if="section.title" class="section-title">
              <span class="title-decorator"></span>
              {{ section.title }}
            </h2>
            <div v-html="section.content" class="section-content"></div>
          </section>
        </div>

        <footer class="policy-footer">
          <div class="footer-info">
            <p>Last updated: <strong>August 12, 2025</strong></p>
            <p>© 2026 China National Center for Bioinformation. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/commons/HeaderItem.vue"

export default {
  name: 'DataTransferAgreement',
  components: {
    Header
  },
  methods: {
    formatId(title) {
      if (!title) return '';
      return title.toLowerCase().replace(/[^a-z0-9]/g, '-');
    }
  },
  data() {
    return {
      sections: [
        {
          title: '1. Definitions',
          content: `
            <ul>
              <li><strong>"Data"</strong> refers to any user-submitted genetically relevant data, including GWAS summary statistics and linkage disequilibrium (LD) reference panels (e.g., PLINK. pgen files) uploaded to the PGS-hub platform.</li>
              <li><strong>"User"</strong> denotes any individual or entity accessing PGS-hub to upload, process, or retrieve Data.</li>
              <li><strong>"Platform"</strong> refers to the PGS-hub web interface and computational infrastructure operated by the China National Center for Bioinformation.</li>
            </ul>
          `
        },
        {
          title: '2. Data Ownership and Submission',
          content: `
            <p>2.1 The User retains full ownership of the Data submitted to the Platform. PGS-hub does not claim intellectual property rights over User Data.</p>
            <p>2.2 The User warrants that they possess legal authority to upload the Data, including compliance with:</p>
            <ul>
              <li>Terms of use for source datasets (e.g., UK Biobank, 1000 Genomes Project).</li>
              <li>Applicable data protection laws (e.g., GDPR, China's Data Security Law and Personal Information Protection Law).</li>
            </ul>
            <p>2.3 The User confirms that:</p>
            <ul>
              <li>Sensitive Data, if uploaded (e.g., individual-level genotype data for custom LD panels), has been de-identified in accordance with international standards to prevent re-identification of individuals.</li>
              <li>No identifiable personal information is included in the Data.</li>
            </ul>
          `
        },
        {
          title: '3. Data Processing and Security',
          content: `
            <p>3.1 <strong>Transmission Security:</strong> All Data is encrypted using <strong>TLS 1.3</strong> during transmission. Sensitive Data is additionally protected using end-to-end encryption.</p>
            <p>3.2 <strong>Storage Security:</strong> Stored Data is protected with <strong>AES-256</strong> encryption at rest. Access is restricted via multi-factor authentication (MFA).</p>
            <p>3.3 <strong>Processing Restrictions:</strong> PGS-hub processes Data solely for the purpose of generating polygenic score (PGS) variant effect sizes. Raw genotype files are permanently deleted immediately after format conversion.</p>
          `
        },
        {
          title: '4. Data Storage and Retention',
          content: `
            <p>4.1 <strong>Default Retention Policy:</strong> User Data is automatically deleted <strong>30 days</strong> after the last access date, aligned with China's Personal Information Protection Law.</p>
            <p>4.2 <strong>User-Controlled Deletion:</strong> Users may request immediate deletion via the "Delete Data" function in the dashboard.</p>
          `
        },
        {
          title: '5. Data Privacy Principles',
          content: `
            <ul>
              <li><strong>Purpose Limitation:</strong> Processed solely for requested PGS computations.</li>
              <li><strong>Data Minimization:</strong> Only minimum necessary data is collected.</li>
              <li><strong>Transparency:</strong> Clear information provided via Privacy Notices.</li>
              <li><strong>Accountability:</strong> Audit logs are maintained for 1 year.</li>
            </ul>
          `
        },
        {
          title: '6. Compliance with Regulations',
          content: `
            <p>6.1 PGS-hub complies with China's Data Security Law and Personal Information Protection Law, and ISO 27001 standards.</p>
            <p>6.2 For international Data, the User is responsible for ensuring compliance with GDPR or relevant regional laws.</p>
          `
        },
        {
          title: '7. User Rights and Obligations',
          content: `
            <p>7.1 <strong>User Rights:</strong> Users may access, correct, delete, or withdraw consent for Data processing at any time.</p>
            <p>7.2 <strong>User Obligations:</strong> Users must ensure submission doesn't violate third-party rights and notify PGS-hub of any security breaches.</p>
          `
        },
        {
          title: '8. Liability and Indemnification',
          content: `
            <p>8.1 PGS-hub is not liable for damages arising from User non-compliance or negligence.</p>
            <p>8.2 User indemnifies PGS-hub against third-party claims related to Data submission.</p>
          `
        },
        {
          title: '9. Agreement Amendment',
          content: `
            <p>Updates will be posted with a <strong>30-day notice period</strong>. Continued use constitutes acceptance of revised terms.</p>
          `
        },
        {
          title: '10. Governing Law and Jurisdiction',
          content: `
            <p>Governed by the laws of the <strong>People's Republic of China</strong>. Disputes resolved via <strong>CIETAC</strong> arbitration in Beijing.</p>
          `
        },
        {
          title: '11. Contact Information',
          content: `
            <div class="contact-box">
              <p>For questions regarding data privacy or transfer, contact the PGS-hub team at:</p>
              <p class="email-link">
                <a href="mailto:wangfei@cncb.ac.cn">wangfei@cncb.ac.cn</a> | 
                <a href="mailto:wangmx@cncb.ac.cn">wangmx@cncb.ac.cn</a>
              </p>
            </div>
          `
        },
        {
          title: '',
          content: `
            <div class="agreement-footer-note">
              By using the PGS-hub Platform, the User acknowledges and agrees to the terms of this DATA PRIVACY POLICY AND TRANSFER AGREEMENT.
            </div>
          `
        },
      ]
    }
  }
};
</script>

<style scoped>
/* 整体布局 */
.page-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: "Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.main-content {
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 20px 60px;
  gap: 40px;
}

/* 侧边栏 */
.sidebar-nav {
  width: 250px;
  flex-shrink: 0;
  display: none; /* 默认在小屏隐藏 */
}

@media (min-width: 1024px) {
  .sidebar-nav {
    display: block;
  }
}

.sidebar-sticky {
  position: sticky;
  top: 110px;
  border-left: 2px solid #e2e8f0;
  padding-left: 20px;
}

.nav-title {
  font-size: 14px;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 1px;
  margin-bottom: 15px;
}

.sidebar-nav ul {
  list-style: none;
  padding: 0;
}

.nav-link {
  display: block;
  padding: 8px 0;
  color: #64748b;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-link:hover {
  color: #3b82f6;
}

/* 主容器 */
.dta-container {
  flex: 1;
  background-color: #ffffff;
  padding: 60px 80px;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

@media (max-width: 768px) {
  .dta-container {
    padding: 30px 20px;
  }
}

/* 标题区 */
.policy-header {
  text-align: center;
  margin-bottom: 40px;
}

.title {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
  margin-bottom: 16px;
  text-transform: uppercase;
}

.version-badge {
  display: inline-block;
  padding: 6px 16px;
  background-color: #eff6ff;
  color: #1d4ed8;
  border-radius: 99px;
  font-size: 14px;
  font-weight: 600;
}

.intro-text {
  margin-top: 24px;
  color: #64748b;
  font-size: 16px;
  line-height: 1.6;
}

.divider {
  border: 0;
  height: 1px;
  background: #e2e8f0;
  margin: 40px 0;
}

/* 章节内容 */
.section {
  margin-bottom: 48px;
  scroll-margin-top: 110px; /* 锚点跳转补偿 */
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 20px;
}

.title-decorator {
  width: 4px;
  height: 20px;
  background-color: #3b82f6;
  margin-right: 12px;
  border-radius: 2px;
}

.section-content {
  line-height: 1.8;
  color: #334155;
  text-align: justify;
}

.section-content p {
  margin-bottom: 16px;
}

.section-content ul {
  padding-left: 24px;
  margin-bottom: 16px;
}

.section-content li {
  margin-bottom: 8px;
}

/* 特定元素美化 */
.contact-box {
  background-color: #f1f5f9;
  padding: 24px;
  border-radius: 8px;
  border-left: 4px solid #94a3b8;
}

.email-link a {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.email-link a:hover {
  text-decoration: underline;
}

.agreement-footer-note {
  margin-top: 20px;
  padding: 20px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  text-align: center;
  font-weight: 600;
  color: #475569;
}

/* 页脚 */
.policy-footer {
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid #e2e8f0;
  color: #94a3b8;
  font-size: 14px;
}

.footer-info {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
</style>